"use client";

import {
  onSnapshot,
  query,
  orderBy,
  where,
  getDoc,
  getDocs,
  doc,
  type Unsubscribe,
  type Query,
} from "firebase/firestore";
import {
  v3GamesCol,
  v3PeopleCol,
  v3OrganizationsCol,
  v3TeamsCol,
  v3LocationsCol,
  v3ExpensesCol,
  v3MileageTripsCol,
  v3TaxConfigCol,
  v3VoiceNotesCol,
  v3ImportJobsCol,
  v3ExportsCol,
  v3UserDoc,
  v3SettingDoc,
  v3InvoicesCol,
  v3AddressesCol,
} from "./collections";
import {
  gameConverter,
  personConverter,
  organizationConverter,
  teamConverter,
  locationConverter,
  expenseConverter,
  mileageTripConverter,
  taxYearConfigConverter,
  voiceNoteConverter,
  importJobConverter,
  exportRecordConverter,
  userProfileConverter,
} from "./converters";
import type {
  V3Game,
  V3Person,
  V3Organization,
  V3Team,
  V3Location,
  V3Expense,
  V3MileageTrip,
  V3TaxYearConfig,
  V3VoiceNote,
  V3ImportJob,
  V3ExportRecord,
  V3UserProfile,
} from "../types.v3";

type Callback<T> = (data: T[], error?: Error) => void;
type SingleCallback<T> = (data: T | null, error?: Error) => void;

function subscribe<T>(
  q: Query,
  cb: Callback<T>,
): Unsubscribe {
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => d.data() as T);
      cb(items);
    },
    (err) => cb([], err),
  );
}

// ---------------------------------------------------------------------------
// Games
// ---------------------------------------------------------------------------

export function subscribeGames(
  uid: string,
  cb: Callback<V3Game>,
): Unsubscribe {
  const q = query(
    v3GamesCol(uid).withConverter(gameConverter),
    orderBy("dateTime", "desc"),
  );
  return subscribe(q, cb);
}

export async function getGame(
  uid: string,
  gameId: string,
): Promise<V3Game | null> {
  const ref = doc(v3GamesCol(uid).withConverter(gameConverter), gameId);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as V3Game) : null;
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export function subscribePeople(
  uid: string,
  cb: Callback<V3Person>,
): Unsubscribe {
  const q = query(
    v3PeopleCol(uid).withConverter(personConverter),
    orderBy("name"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export function subscribeOrganizations(
  uid: string,
  cb: Callback<V3Organization>,
): Unsubscribe {
  const q = query(
    v3OrganizationsCol(uid).withConverter(organizationConverter),
    orderBy("name"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

export function subscribeTeams(
  uid: string,
  cb: Callback<V3Team>,
): Unsubscribe {
  const q = query(
    v3TeamsCol(uid).withConverter(teamConverter),
    orderBy("name"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export function subscribeLocations(
  uid: string,
  cb: Callback<V3Location>,
): Unsubscribe {
  const q = query(
    v3LocationsCol(uid).withConverter(locationConverter),
    orderBy("name"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export function subscribeExpenses(
  uid: string,
  cb: Callback<V3Expense>,
  taxYear?: string,
): Unsubscribe {
  let q;
  if (taxYear) {
    q = query(
      v3ExpensesCol(uid).withConverter(expenseConverter),
      where("taxYear", "==", taxYear),
      orderBy("date", "desc"),
    );
  } else {
    q = query(
      v3ExpensesCol(uid).withConverter(expenseConverter),
      orderBy("date", "desc"),
    );
  }
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Mileage Trips
//
// @deprecated Parallel mileage model not used in UI. Prefer reading
// `game.miles` + `game.mileageReconciledAt` from `subscribeGames`. See
// `design/finance-lifecycle-review.md` §5 Phase 1c.
// ---------------------------------------------------------------------------

/** @deprecated See comment block above. */
export function subscribeMileageTrips(
  uid: string,
  cb: Callback<V3MileageTrip>,
  taxYear?: string,
): Unsubscribe {
  let q;
  if (taxYear) {
    q = query(
      v3MileageTripsCol(uid).withConverter(mileageTripConverter),
      where("taxYear", "==", taxYear),
      orderBy("tripDate", "desc"),
    );
  } else {
    q = query(
      v3MileageTripsCol(uid).withConverter(mileageTripConverter),
      orderBy("tripDate", "desc"),
    );
  }
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Tax Config
// ---------------------------------------------------------------------------

export function subscribeTaxConfigs(
  uid: string,
  cb: Callback<V3TaxYearConfig>,
): Unsubscribe {
  const col = v3TaxConfigCol(uid).withConverter(taxYearConfigConverter);
  return subscribe(query(col), cb);
}

// ---------------------------------------------------------------------------
// Voice Notes
// ---------------------------------------------------------------------------

export function subscribeVoiceNotes(
  uid: string,
  cb: Callback<V3VoiceNote>,
): Unsubscribe {
  const q = query(
    v3VoiceNotesCol(uid).withConverter(voiceNoteConverter),
    orderBy("createdAt", "desc"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Import Jobs
// ---------------------------------------------------------------------------

export function subscribeImportJobs(
  uid: string,
  cb: Callback<V3ImportJob>,
): Unsubscribe {
  const q = query(
    v3ImportJobsCol(uid).withConverter(importJobConverter),
    orderBy("createdAt", "desc"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// Export Records
// ---------------------------------------------------------------------------

export function subscribeExportRecords(
  uid: string,
  cb: Callback<V3ExportRecord>,
): Unsubscribe {
  const q = query(
    v3ExportsCol(uid).withConverter(exportRecordConverter),
    orderBy("createdAt", "desc"),
  );
  return subscribe(q, cb);
}

// ---------------------------------------------------------------------------
// User Profile (single doc)
// ---------------------------------------------------------------------------

export function subscribeUserProfile(
  uid: string,
  cb: SingleCallback<V3UserProfile>,
): Unsubscribe {
  const docRef = v3UserDoc(uid).withConverter(userProfileConverter);
  return onSnapshot(
    docRef,
    (snap) => {
      if (snap.exists()) {
        cb(snap.data());
      } else {
        cb(null);
      }
    },
    (err) => cb(null, err),
  );
}

export async function getUserProfile(
  uid: string,
): Promise<V3UserProfile | null> {
  const docRef = v3UserDoc(uid).withConverter(userProfileConverter);
  const snap = await getDoc(docRef);
  return snap.exists() ? snap.data() : null;
}

// ---------------------------------------------------------------------------
// Settings (key-value docs: gameDefaults, calendarSync, debug, subscription, etc.)
// ---------------------------------------------------------------------------

export function subscribeSetting(
  uid: string,
  key: string,
  cb: (data: Record<string, unknown> | null, error?: Error) => void,
): Unsubscribe {
  const docRef = v3SettingDoc(uid, key);
  return onSnapshot(
    docRef,
    (snap) => {
      cb(snap.exists() ? (snap.data() as Record<string, unknown>) : null);
    },
    (err) => cb(null, err),
  );
}

export async function getSetting(
  uid: string,
  key: string,
): Promise<Record<string, unknown> | null> {
  const docRef = v3SettingDoc(uid, key);
  const snap = await getDoc(docRef);
  return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
}

// ---------------------------------------------------------------------------
// Addresses (user saved addresses: home, work, etc.)
// ---------------------------------------------------------------------------

export function subscribeAddresses(
  uid: string,
  cb: (data: Array<{ id: string; [key: string]: unknown }>) => void,
): Unsubscribe {
  return onSnapshot(v3AddressesCol(uid), (snap) => {
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    cb(list);
  });
}

export async function getAddresses(
  uid: string,
): Promise<Array<{ id: string; [key: string]: unknown }>> {
  const snap = await getDocs(v3AddressesCol(uid));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// ---------------------------------------------------------------------------
// Invoices (Stripe billing; keyed by Stripe invoice id)
// ---------------------------------------------------------------------------

export function subscribeInvoices(
  uid: string,
  cb: (invoices: Array<{ id: string; [key: string]: unknown }>) => void,
): Unsubscribe {
  const q = query(v3InvoicesCol(uid), orderBy("date", "desc"));
  return onSnapshot(
    q,
    (snap) => {
      const invoices = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      cb(invoices);
    },
    () => cb([]),
  );
}
